import React from 'react';
import { ArchitectureSystem } from '../types/system';
import { StatutoryActorMaster, ComplianceDomainMaster, CommencementTrancheMaster } from '../types/masterData';
import {
  Search,
  Filter,
  RotateCcw,
  Users,
  Calendar,
  Layers,
  CheckCircle2,
  UserCheck,
  Cpu
} from 'lucide-react';

interface FilterPanelProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  actorFilter: string;
  onActorChange: (a: string) => void;
  trancheFilter: string;
  onTrancheChange: (t: string) => void;
  domainFilter: string;
  onDomainChange: (d: string) => void;
  statusFilter: string;
  onStatusChange: (s: string) => void;
  ownerFilter: string;
  onOwnerChange: (o: string) => void;
  systemFilter: string;
  onSystemChange: (sys: string) => void;
  systems: ArchitectureSystem[];
  actors?: StatutoryActorMaster[];
  tranches?: CommencementTrancheMaster[];
  domains?: ComplianceDomainMaster[];
  owners?: string[];
  onResetFilters: () => void;
  hasActiveFilters: boolean;
  totalFiltered: number;
  totalItems: number;
  isControlsMode: boolean;
}

export default function FilterPanel({
  searchQuery,
  onSearchChange,
  actorFilter,
  onActorChange,
  trancheFilter,
  onTrancheChange,
  domainFilter,
  onDomainChange,
  statusFilter,
  onStatusChange,
  ownerFilter,
  onOwnerChange,
  systemFilter,
  onSystemChange,
  systems = [],
  actors,
  tranches,
  domains,
  owners,
  onResetFilters,
  hasActiveFilters,
  totalFiltered,
  totalItems,
  isControlsMode
}: FilterPanelProps) {
  return (
    <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs space-y-4">
      
      {/* Top Search & Filter Reset Row */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        
        {/* Search Input */}
        <div className="flex-1 relative flex items-center">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder={
              isControlsMode
                ? "Search controls (e.g. OBL-01, consent withdrawal, retention, SYS-002, CISO)..."
                : "Search statutory provisions (e.g. Section 6, Rule 3, Consent Manager, breach)..."
            }
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl pl-10 pr-4 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              Clear
            </button>
          )}
        </div>

        {/* Results Counter & Reset Button */}
        <div className="flex items-center justify-between md:justify-end gap-3">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Showing <strong className="text-slate-900 dark:text-slate-100">{totalFiltered}</strong> of {totalItems} items
          </span>

          {hasActiveFilters && (
            <button
              onClick={onResetFilters}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/50 border border-rose-200 dark:border-rose-900/40 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset Filters</span>
            </button>
          )}
        </div>

      </div>

      {/* Multi-Dimensional Filter Dropdowns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 pt-1">
        
        {/* Statutory Actor Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <Users className="w-3 h-3 text-accent" />
            <span>Statutory Actor</span>
          </label>
          <select
            value={actorFilter}
            onChange={(e) => onActorChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
          >
            <option value="all">All Actors</option>
            {actors && actors.length > 0 ? (
              actors.map(a => (
                <option key={a.id} value={a.name}>{a.name}</option>
              ))
            ) : (
              <>
                <option value="Data Fiduciary">Data Fiduciary</option>
                <option value="Significant Data Fiduciary">Significant Data Fiduciary (SDF)</option>
                <option value="Data Processor">Data Processor</option>
                <option value="Consent Manager">Consent Manager</option>
                <option value="Data Principal">Data Principal</option>
                <option value="Central Government">Central Government / Board</option>
              </>
            )}
          </select>
        </div>

        {/* Tranche Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <Calendar className="w-3 h-3 text-accent" />
            <span>Effective Tranche</span>
          </label>
          <select
            value={trancheFilter}
            onChange={(e) => onTrancheChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
          >
            <option value="all">All Tranches</option>
            {tranches && tranches.length > 0 ? (
              tranches.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.date}) — {t.status}</option>
              ))
            ) : (
              <>
                <option value="2025-11-13">Tranche 1 (13 Nov 2025) — In Force</option>
                <option value="2026-11-13">Tranche 2 (13 Nov 2026) — Consent Mgrs</option>
                <option value="2027-05-13">Tranche 3 (13 May 2027) — Core Duties</option>
              </>
            )}
          </select>
        </div>

        {/* Domain Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <Layers className="w-3 h-3 text-accent" />
            <span>Domain</span>
          </label>
          <select
            value={domainFilter}
            onChange={(e) => onDomainChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
          >
            <option value="all">All Domains</option>
            {domains && domains.length > 0 ? (
              domains.map(d => (
                <option key={d.id} value={d.slug}>{d.title}</option>
              ))
            ) : (
              <>
                <option value="scope">Scope & Territorial Perimeter</option>
                <option value="purpose">Grounds & Lawful Purpose</option>
                <option value="notice-consent">Notice & Consent Architecture</option>
                <option value="withdrawal">Consent Withdrawal & Cessation</option>
                <option value="consent-managers">Consent Managers & Interoperability</option>
                <option value="rights">Data Principal Rights & Grievance</option>
                <option value="children">Children Data & Parental Consent</option>
                <option value="retention">Retention Limits & Erasure</option>
                <option value="security">Security Safeguards & Access</option>
                <option value="breach">Breach Detection & Notification</option>
                <option value="processors">Processors & Third-Party Risk</option>
                <option value="transfers">Cross-Border Transfers</option>
                <option value="sdf">Significant Data Fiduciary (SDF)</option>
                <option value="exemptions">Statutory Exemptions</option>
                <option value="enforcement">Board Oversight & Enforcement</option>
              </>
            )}
          </select>
        </div>

        {/* Target Architecture System Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <Cpu className="w-3 h-3 text-indigo-500" />
            <span>Target System</span>
          </label>
          <select
            value={systemFilter}
            onChange={(e) => onSystemChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
          >
            <option value="all">All Systems ({systems.length})</option>
            {systems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.id}: {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Applicability Decision Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-accent" />
            <span>Decision Status</span>
          </label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
          >
            <option value="ALL">All Decision States</option>
            <option value="APPLICABLE">Applicable (In Scope)</option>
            <option value="NOT_APPLICABLE">Not Applicable (Exempted)</option>
            <option value="UNRESOLVED">Unresolved (Requires Review)</option>
            <option value="UNASSESSED">Unassessed (Pending)</option>
          </select>
        </div>

        {/* Assigned Owner Filter */}
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <UserCheck className="w-3 h-3 text-accent" />
            <span>Control Owner</span>
          </label>
          <select
            value={ownerFilter}
            onChange={(e) => onOwnerChange(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
          >
            <option value="all">All Owners</option>
            {owners && owners.length > 0 ? (
              owners.map(o => (
                <option key={o} value={o}>{o}</option>
              ))
            ) : (
              <>
                <option value="Legal">Legal / General Counsel</option>
                <option value="Security">Security / CISO</option>
                <option value="Engineering">Engineering / Architecture</option>
                <option value="DPO">Data Protection Officer</option>
                <option value="Product">Product / UX</option>
                <option value="Procurement">Procurement / Vendor Risk</option>
              </>
            )}
          </select>
        </div>

      </div>

    </div>
  );
}
